/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import {
  notificationRunsService, type Delivery, type RunRecipient, type RuleResult,
} from '../../services/notification-runs.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate } from '../../hooks/usePermissions';
import { useOutcomeReasonLabel } from '../../hooks/useOutcomeReasonLabel';
import { JOB_NAMES } from '../../constants/jobNames';
import { getUserMasterId } from '../../utils/current-user';
import WhatsAppProgress from '../../components/notifications/whatsapp-progress';
import SideDrawer from '../../components/common/side-drawer';
import ChannelIcons from '../../components/common/channel-icons';

const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '-');

// QUEUED is an email the outbox has accepted and not yet sent — in flight, like
// PENDING, and deliberately NOT green: accepted is not sent, and showing it as
// success is exactly how a failing mail server went unnoticed.
const deliverySeverity = (s?: string | null) =>
  s === 'SENT' ? 'success' : s === 'FAILED' ? 'danger'
    : (s === 'PENDING' || s === 'QUEUED') ? 'warning' : 'secondary';

const slaSeverity = (s?: string | null) =>
  s === 'MET' ? 'success' : s === 'BREACHED' ? 'danger' : 'info';

/** Who an entry is, in the words a reader recognises.
 *
 *  The engine resolves people to ids, and a screen that prints one answers "who was
 *  this sent to?" with a hex string — the one question it exists to answer. The server
 *  resolves `displayName` from the personnel record, falling back to the login id; an
 *  external party has neither, so their address is their name. */
const who = (r: {
  displayName?: string | null;
  userId?: string | null;
  contact?: Record<string, string> | null;
}) => r.displayName || r.userId || r.contact?.email || r.contact?.phone || '-';

/** Fields that are the message, in the order a reader wants them. */
const HEADINGS = ['subject', 'title'];
const BODIES = ['body', 'htmlBody', 'caption'];

/** Plumbing, not the message: hidden from what a delivery shows it sent. */
const IDENTIFIERS = ['templateId', 'dltTemplateId', 'fileId', 'mediaFileId',
  'notificationMasterId', 'recipientMasterId', 'contactId', 'userId', 'personnelMasterId',
  // How the in-app row draws itself. It is styling for another screen, not
  // anything this send said.
  'icon'];
const OBJECT_ID = /^[0-9a-f]{24}$/i;

const isUrl = (v: unknown): v is string => typeof v === 'string' && /^https?:\/\//i.test(v);
const isHtml = (v?: string | null) => /<[a-z][\s\S]*>/i.test(v || '');

/** Keys that are a picture, plus anything whose URL ends in one. */
const IMAGE_KEYS = ['image', 'imageUrl', 'mediaUrl', 'thumbnailUrl', 'picture', 'snapshotUrl'];
const looksLikeImage = (k: string, v: string) =>
  IMAGE_KEYS.includes(k) || /\.(png|jpe?g|gif|webp|svg)$/i.test(v.split('?')[0]);

interface Attachment { url?: string; filename?: string; mimeType?: string }
/** `[{url, filename, mimeType}]` as the mailer writes it — anything else is not one. */
const asAttachments = (v: unknown): Attachment[] =>
  Array.isArray(v) && v.length > 0
    && v.every((x) => !!x && typeof x === 'object' && typeof (x as Attachment).url === 'string')
    ? (v as Attachment[]) : [];

const humanise = (k: string) =>
  k.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

/** Is this something the panel can show, or only hand over? */
const isImageFile = (a: Attachment) =>
  (a.mimeType || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test((a.url || '').split('?')[0]);
const isPdfFile = (a: Attachment) =>
  (a.mimeType || '') === 'application/pdf' || /\.pdf$/i.test((a.url || '').split('?')[0]);

/** A file name we can show, even when the URL is a signed one a mile long. */
const fileNameOf = (a: Attachment) =>
  a.filename || decodeURIComponent((a.url || '').split('?')[0].split('/').pop() || 'attachment');

/**
 * What this delivery actually sent.
 *
 * Not the template — the template is what would be sent to anybody, and every
 * placeholder in it is still a placeholder. This is the text that reached this
 * person, rendered against the trigger payload and pinned at send time, which
 * is the only version that still answers "what did they read?" after somebody
 * edits the master.
 *
 * And it is shown the way they saw it: the picture as a picture, the attachment
 * as a file you can open, the HTML body rendered rather than escaped. A signed
 * blob URL printed as four lines of grey text is the same fact, but nobody
 * reading this screen can tell from it whether the right image went out.
 */
const SentMessage = ({ content }: { content: Record<string, unknown> }) => {
  const heading = HEADINGS.map((k) => content[k]).find((v) => typeof v === 'string' && v.trim());
  const body = BODIES.map((k) => content[k]).find((v) => typeof v === 'string' && v.trim());

  // Anything that is not the words: the WhatsApp message type, the variables that
  // filled an operator template. Shown because on SMS they ARE what went on the wire —
  // the body is only what we meant to say.
  //
  // Except the ids. `templateId: 6a8e7c4b…` tells a reader nothing they can act on and
  // crowds out what the message actually said; whoever needs the row can open the
  // template. Anything whose value is a bare ObjectId goes the same way, so a field
  // added later does not quietly put one back on the screen.
  const rest = Object.entries(content).filter(
    ([k, v]) => ![...HEADINGS, ...BODIES].includes(k)
      && v !== null && v !== '' && v !== undefined
      && !IDENTIFIERS.includes(k)
      && !(typeof v === 'string' && OBJECT_ID.test(v)),
  );

  // The file being looked at, if any. It opens here rather than in a new tab:
  // checking what went out is part of reading this delivery, and a tab away is
  // a place you have to come back from — with a signed URL that may by then
  // have expired out from under you.
  const [preview, setPreview] = useState<Attachment | null>(null);

  const images = rest.filter(([k, v]) => isUrl(v) && looksLikeImage(k, v)) as [string, string][];
  const files = rest.flatMap(([, v]) => asAttachments(v));
  const shown = new Set([...images.map(([k]) => k)]);
  const fields = rest.filter(([k, v]) => !shown.has(k) && !asAttachments(v).length);

  return (
    <div className="rounded border border-line bg-canvas dark:bg-gray-900/40 p-3 flex flex-col gap-2">
      {heading ? <p className="text-sm font-semibold text-ink">{heading as string}</p> : null}

      {body ? (
        isHtml(body as string) ? (
          <div
            className="text-xs text-gray-700 dark:text-gray-300 max-w-[70ch]"
            /* Server-rendered template HTML, sanitised against stored XSS — the
               same treatment the inbox gives a message body. */
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body as string) }}
          />
        ) : (
          <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words max-w-[70ch]">
            {body as string}
          </p>
        )
      ) : (
        <span className="text-xs text-gray-400">No text was rendered for this delivery.</span>
      )}

      {/* The picture that went with it, shown rather than described. Capped so a
          tall frame cannot push everything under it off the panel, and linked so
          the full-size original is one click away. */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map(([k, v]) => (
            <button
              key={k}
              type="button"
              title={`${humanise(k)} — click to enlarge`}
              onClick={() => setPreview({ url: v, filename: humanise(k), mimeType: 'image/*' })}
              className="rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
            >
              <img
                src={v}
                alt={humanise(k)}
                loading="lazy"
                className="max-h-56 w-auto max-w-full rounded-lg ring-1 ring-gray-200 hover:ring-blue-400 dark:ring-gray-700"
              />
            </button>
          ))}
        </div>
      )}

      {/* What was attached, as files rather than as a line of JSON. */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {files.map((f, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setPreview(f)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700/40"
            >
              <i className="pi pi-paperclip text-xs text-gray-400" />
              <span className="max-w-[18rem] truncate">{fileNameOf(f)}</span>
              {f.mimeType && <span className="text-gray-400">{f.mimeType}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Everything else the channel carried, labelled. A URL is a link rather
          than a wrapped wall of signature. */}
      {fields.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 pt-1 border-t border-line">
          {fields.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-xs text-gray-400 whitespace-nowrap">{humanise(k)}</dt>
              <dd className="min-w-0 text-xs text-gray-600 dark:text-gray-300 break-words">
                {isUrl(v) ? (
                  <a href={v} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline dark:text-blue-400 break-all">
                    {v.split('?')[0]}
                  </a>
                ) : typeof v === 'object' ? (
                  <span className="font-mono break-all">{JSON.stringify(v)}</span>
                ) : String(v)}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/* The file, opened over the panel. A picture and a PDF are shown as they
          are; anything else has no renderer here, so it says what it is and
          offers itself for download rather than pretending it can be read. */}
      <Dialog
        visible={!!preview}
        onHide={() => setPreview(null)}
        header={preview ? fileNameOf(preview) : ''}
        style={{ width: 'auto', maxWidth: '90vw' }}
        dismissableMask
        data-testid="NotificationRunView.FilePreview"
      >
        {preview && (
          isImageFile(preview) ? (
            <div className="flex justify-center">
              <img src={preview.url} alt={fileNameOf(preview)} className="max-h-[75vh] max-w-full object-contain" />
            </div>
          ) : isPdfFile(preview) ? (
            <iframe
              src={preview.url}
              title={fileNameOf(preview)}
              className="h-[75vh] w-[80vw] max-w-full rounded border border-line"
            />
          ) : (
            <div className="flex flex-col items-start gap-2 p-2">
              <p className="text-sm text-ink-muted">
                {preview.mimeType || 'This file'} cannot be shown here.
              </p>
              <a
                href={preview.url}
                download={fileNameOf(preview)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
              >
                <i className="pi pi-download text-xs text-gray-400" />
                Download {fileNameOf(preview)}
              </a>
            </div>
          )
        )}
      </Dialog>
    </div>
  );
};

/** The one line a closed row shows: what this delivery said. */
const previewOf = (c?: Record<string, unknown> | null): string => {
  const pick = (keys: string[]) =>
    keys.map((k) => c?.[k]).find((v) => typeof v === 'string' && v.trim()) as string | undefined;
  return (pick(HEADINGS) || pick(BODIES) || '').replace(/\s+/g, ' ').trim();
};

/** One attempt's `detail`, in a line: the provider's error if it gave one, its
 *  own fields otherwise, capped so a stack trace cannot take over the panel. */
const detailOf = (detail?: Record<string, unknown> | null): string => {
  if (!detail) return '';
  const text = typeof detail.error === 'string' && detail.error.trim()
    ? detail.error
    : Object.entries(detail)
      .filter(([, v]) => v !== null && v !== '' && v !== undefined)
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
      .join(' · ');
  return text.length > 240 ? `${text.slice(0, 240)}…` : text;
};

const Section = ({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) => (
  <div className="ds-card p-3 mb-3">
    <label className="text-xs text-ink-muted block mb-1">
      {title}
      {hint ? <span className="block text-gray-400">{hint}</span> : null}
    </label>
    {children}
  </div>
);

const NotificationRunView = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({ entity: 'notification run', basePath: '/notification-runs' });
  const canUpdate = useCanUpdate(JOB_NAMES.NOTIFICATION_MASTER);
  const reasonLabel = useOutcomeReasonLabel();
  const toast = useRef<Toast>(null);
  const [memo, setMemo] = useState('');
  // Which delivery is open in the drawer, by index. The list holds its place
  // while one is read, as the inbox does — five deliveries expanded inline is
  // the wall of text this screen used to be.
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['notificationRun', id],
    queryFn: () => notificationRunsService.getById(id!),
    enabled: isReady && !!id,
  });

  // Actions and read-state are PERSONAL: the API resolves them against the
  // calling user's own entry on this fire, and 404s for anyone who was not a
  // recipient. This screen is an administrator's view of what a notification
  // did, so most viewers are not recipients — asking anyway would fail on
  // nearly every visit, and "Mark read" would error on someone else's run.
  //
  // The document already lists its recipients, so whether to offer these is
  // answered locally rather than by a request expected to fail.
  const isRecipient = (data?.recipients || []).some(
    (r) => String(r.userId || '') === getUserMasterId(),
  );

  // Re-derived now rather than read off the document: a recipient's state and
  // their grants both change after a fire, so what they may do has to be asked
  // at the moment they look.
  const { data: available } = useQuery({
    queryKey: ['notificationRun', id, 'actions'],
    queryFn: () => notificationRunsService.availableActions(id!),
    enabled: isReady && !!id && isRecipient,
    retry: false,
  });

  const takeAction = useMutation({
    mutationFn: (action: { actionCode: string; isMemoRequired?: boolean }) => {
      if (action.isMemoRequired && !memo.trim()) {
        throw new Error('This action needs a note before it can be taken.');
      }
      return notificationRunsService.takeAction(id!, action.actionCode, memo || undefined);
    },
    onSuccess: () => {
      setMemo('');
      toast.current?.show({ severity: 'success', summary: 'Recorded', life: 3000 });
      refetch();
    },
    onError: (e: any) => toast.current?.show({ severity: 'error', summary: 'Action failed', detail: extractErrorMessage(e, 'Action failed'), life: 8000 }),
  });

  const markRead = useMutation({
    mutationFn: () => notificationRunsService.markRead(id!),
    onSuccess: () => { toast.current?.show({ severity: 'success', summary: 'Marked read', life: 2000 }); refetch(); },
    onError: (e: any) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Could not mark read'), life: 6000 }),
  });

  const cancel = useMutation({
    mutationFn: () => notificationRunsService.cancel(id!),
    onSuccess: () => { toast.current?.show({ severity: 'success', summary: 'Cancelled', life: 3000 }); refetch(); },
    onError: (e: any) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Cancel failed'), life: 8000 }),
  });

  // Only the failed deliveries by default — replaying to everybody re-sends to
  // people who already received it.
  const replay = useMutation({
    mutationFn: () => notificationRunsService.replay(id!),
    onSuccess: () => { toast.current?.show({ severity: 'success', summary: 'Replayed', detail: 'Failed deliveries were re-attempted.', life: 4000 }); refetch(); },
    onError: (e: any) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Replay failed'), life: 8000 }),
  });

  if (isLoading) return <PageSkeleton />;
  if (isError || !data) return (
    <div className="p-4">
      <Message severity="error" text={extractErrorMessage(error, 'Notification not found')} className="mb-4 w-full" />
      <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigateToList()} />
    </div>
  );

  const results: RuleResult[] = data.ruleResults || [];
  const recipients: RunRecipient[] = data.recipients || [];
  const deliveries: Delivery[] = data.deliveries || [];
  // Who a delivery was for is on the recipient it references. The delivery
  // carries only the ref, so there is one copy of an identity and no second one
  // to disagree with it.
  const recipientByRef = new Map(recipients.map((r) => [r.recipientRef, r]));
  const isTerminal = ['COMPLETE', 'CANCELLED', 'EXPIRED'].includes(data.executionStatus || '');
  const opened = openIdx !== null ? deliveries[openIdx] : undefined;

  /**
   * One delivery, said once.
   *
   * `status` answers "did our send succeed?" and `outcome` what became of it; on
   * a delivery that worked they are the same fact in two words. The sweep's
   * reason is usually the row's reason repeated, and the provider's error is
   * often a louder copy of both. Each is kept only where it adds something.
   */
  const facts = (d: Delivery) => {
    const reason = d.reasonCode ? reasonLabel(d.reasonCode) : '';
    const stopped = d.retryStopped ? reasonLabel(d.retryStopped.reasonCode) : '';
    return {
      outcome: d.outcome && d.outcome !== d.status
        && !(d.status === 'SENT' && d.outcome === 'DELIVERED') ? d.outcome : '',
      reason,
      lastError: d.lastError && d.lastError !== reason ? d.lastError : '',
      stoppedLabel: !stopped || stopped === reason ? 'Retries stopped' : `Retries stopped: ${stopped}`,
    };
  };

  return (
    <div className="p-3" data-testid="SCR-NotificationRun-View">
      <Toast ref={toast} position="top-right" />
      <ScreenHeader icon="pi pi-send" title="Notification" onBack={() => navigateToList()} />

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <div>
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            <h2 className="text-base font-semibold text-ink font-mono">
              {data.configuration?.notificationCode || data.configuration?.notificationMasterId || data._id}
            </h2>
            <Tag value={data.executionStatus || '-'} severity={data.executionStatus === 'COMPLETE' ? 'success' : data.executionStatus === 'CANCELLED' ? 'danger' : 'info'} className="text-xs px-1.5 py-0.5" />
            {data.outcome && <Tag value={data.outcome} severity="secondary" className="text-xs px-1.5 py-0.5" />}
          </div>
          <p className="text-xs text-ink-muted">
            Fired {fmt(data.createdAt)}
            {data.trigger?.correlationId ? <span className="font-mono"> · {data.trigger.correlationId}</span> : null}
          </p>
        </div>
        <div className="flex gap-2">
          {isRecipient && (
            <Button label="Mark read" icon="pi pi-envelope" size="small" severity="secondary" text
              onClick={() => markRead.mutate()} disabled={markRead.isPending} />
          )}
          {canUpdate && deliveries.some((d) => d.status === 'FAILED') && (
            <Button label="Replay failed" icon="pi pi-replay" size="small" severity="secondary" outlined
              onClick={() => replay.mutate()} disabled={replay.isPending} />
          )}
          {canUpdate && !isTerminal && (
            <Button label="Cancel" icon="pi pi-times" size="small" severity="danger" outlined
              onClick={() => cancel.mutate()} disabled={cancel.isPending} />
          )}
        </div>
      </div>

      {isRecipient && (available || []).length > 0 && (
        <div className="ds-card p-3 mb-3">
          <label className="text-xs text-ink-muted block mb-2">
            Actions available to you
            <span className="block text-gray-400">
              You only see actions you are allowed to take right now.
            </span>
          </label>
          {(available || []).some((a) => a.isMemoRequired) && (
            <InputTextarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={2}
              placeholder="Note (required by some actions)" className="w-full mb-2 text-sm"
              data-testid="NotificationRunView.Memo" />
          )}
          <div className="flex gap-2 flex-wrap">
            {(available || []).map((action) => (
              <Button
                key={action.actionCode}
                label={action.actionCode}
                size="small"
                severity={action.isDefault ? undefined : 'secondary'}
                outlined={!action.isDefault}
                disabled={takeAction.isPending}
                data-testid={`NotificationRunView.Action.${action.actionCode}`}
                onClick={() => takeAction.mutate(action)}
              />
            ))}
          </div>
        </div>
      )}

      {data.reasonCode && (
        <Message severity="warn" className="w-full mb-2" text={`Outcome reason: ${reasonLabel(data.reasonCode)}`} />
      )}

      <div className="ds-card p-3 grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
        {[
          // `total`, which is what the fire stores. It was read as `recipients`,
          // a key nothing writes — so this counted every fire's audience as 0.
          ['Recipients', data.counts?.total ?? 0, 'text-ink'],
          ['Delivered', data.counts?.delivered ?? 0, 'text-green-600 dark:text-green-400'],
          ['Failed', data.counts?.failed ?? 0, 'text-red-600 dark:text-red-400'],
          ['Suppressed', data.counts?.suppressed ?? 0, 'text-amber-600 dark:text-amber-400'],
          ['Expires', data.expiresAt ? fmt(data.expiresAt) : 'Never', 'text-ink text-sm'],
        ].map(([label, value, cls]) => (
          <div key={label as string}>
            <label className="text-xs text-ink-muted block">{label as string}</label>
            <p className={`font-semibold ${cls as string}`}>{value as any}</p>
          </div>
        ))}
      </div>

      <Section title={`Recipient results (${results.length})`} hint="What each configured recipient resolved to — including the ones that found nobody.">
        {results.length ? (
          <div className="flex flex-col gap-1">
            {results.map((r, i) => (
              <div key={i} className="text-sm flex items-center gap-2 flex-wrap">
                {/* The audience's code, never its id: the server resolves it on read,
                    and a row that falls back to a hex string is the one nobody can act on. */}
                <span className="text-xs font-medium text-ink">
                  {r.recipientCode || 'audience'}
                </span>
                {r.outcome && <Tag value={r.outcome} severity={r.outcome === 'RESOLVED' ? 'success' : 'warning'} className="text-xs px-1.5 py-0.5" />}
                <span className="text-xs text-gray-400">{r.resolvedCount ?? 0} resolved</span>
                {r.reasonCode && <span className="text-xs text-gray-500" title={r.reasonCode}>{reasonLabel(r.reasonCode)}</span>}
              </div>
            ))}
          </div>
        ) : <span className="text-sm text-gray-400">-</span>}
      </Section>

      <Section title={`Recipients (${recipients.length})`} hint="One entry per resolved person, with the recipient that found them.">
        {recipients.length ? (
          <div className="flex flex-col gap-1">
            {recipients.map((r, i) => (
              <div key={i} className="text-sm flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-ink">{who(r)}</span>
                {r.partyType && <Tag value={r.partyType} severity="secondary" className="text-xs px-1.5 py-0.5" />}
                {r.state && <Tag value={r.state} severity="info" className="text-xs px-1.5 py-0.5" />}
                {r.selectedActionCode && (
                  <span className="text-xs text-gray-500">{r.selectedActionCode} · {fmt(r.actionedAt)}</span>
                )}
              </div>
            ))}
          </div>
        ) : <span className="text-sm text-gray-400">-</span>}
      </Section>

      <Section
        title={`Deliveries (${deliveries.length})`}
        hint="One per recipient and channel. Open one to read the message it actually sent — rendered against the payload, not the template it came from."
      >
        {deliveries.length ? (
          <div className="flex flex-col gap-1.5">
            {deliveries.map((d, i) => {
              const f = facts(d);
              const preview = previewOf(d.renderedContent);
              const isOpen = openIdx === i;
              return (
                <div
                  key={i}
                  role="button"
                  tabIndex={0}
                  aria-current={isOpen ? 'true' : undefined}
                  className={`rounded-lg border px-3 py-2 cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 ${
                    isOpen
                      ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-500/40 dark:border-blue-700 dark:bg-blue-900/30'
                      : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/40'}`}
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenIdx(isOpen ? null : i); }
                  }}
                  data-testid={`NotificationRunView.Delivery.${i}`}
                >
                  <div className="flex items-center gap-2">
                    {/* The channel in the inbox's own chip — a bordered grey pill
                        carrying the channel's icon. It was a solid blue tag beside
                        a solid red one, which put the same weight on which channel
                        this was as on the fact that it failed. */}
                    <ChannelIcons channels={[d.channel || '']} variant="chips" empty="no channel" />
                    <span className="min-w-0 flex-1 truncate text-xs font-medium text-ink">
                      {who(recipientByRef.get(d.recipientRef) || {})}
                    </span>
                    <Tag value={d.status || '-'} severity={deliverySeverity(d.status)} className="text-xs px-1.5 py-0.5" />
                    {f.outcome && <span className="text-xs text-gray-500">{f.outcome}</span>}
                    {d.sla?.state && (
                      <Tag value={`SLA ${d.sla.state}`} severity={slaSeverity(d.sla.state)} className="text-xs px-1.5 py-0.5" />
                    )}
                    <i className="pi pi-chevron-right text-xs text-gray-300 dark:text-gray-600" />
                  </div>
                  {/* One line, truncated: what it said, or why it did not go. The
                      whole of either is one click away, and a row that carries all
                      of it is a row nobody can scan past. */}
                  {(f.reason || f.lastError || preview) && (
                    <p className={`mt-1 truncate text-xs ${f.reason || f.lastError
                      ? 'text-amber-600 dark:text-amber-400' : 'text-ink-muted'}`}>
                      {f.reason || f.lastError || preview}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ) : <span className="text-sm text-gray-400">-</span>}
      </Section>

      {/* One delivery, opened over the list rather than expanded inside it —
          the same drawer the inbox opens a message in. */}
      <SideDrawer
        open={!!opened}
        onClose={() => setOpenIdx(null)}
        ariaLabel="Delivery"
        testId="NotificationRunView.Delivery.Drawer"
        header={opened && (
          <header className="shrink-0 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold leading-snug text-ink">
                  {who(recipientByRef.get(opened.recipientRef) || {})}
                </h3>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <ChannelIcons channels={[opened.channel || '']} variant="chips" empty="no channel" />
                  <Tag value={opened.status || '-'} severity={deliverySeverity(opened.status)} className="text-xs px-1.5 py-0.5" />
                  {facts(opened).outcome && (
                    <span className="text-xs text-gray-500">{facts(opened).outcome}</span>
                  )}
                  {opened.sla?.state && (
                    <Tag value={`SLA ${opened.sla.state}`} severity={slaSeverity(opened.sla.state)} className="text-xs px-1.5 py-0.5" />
                  )}
                  {opened.retryStopped && (
                    // Why the retry sweep stopped. "They asked" and "we gave up"
                    // are different facts, and this is the only place they show.
                    <Tag value={facts(opened).stoppedLabel} severity="danger" className="text-xs px-1.5 py-0.5" />
                  )}
                </div>
              </div>
              <Button aria-label="Close" icon="pi pi-times" text rounded size="small"
                onClick={() => setOpenIdx(null)} data-testid="NotificationRunView.Delivery.Drawer.Close" />
            </div>
          </header>
        )}
        footer={opened && ((opened.retry?.count ?? 0) > 0 || !!opened.deliveredAt || !!opened.sla?.dueAt) && (
          <footer className="shrink-0 border-t border-gray-200 bg-gray-50 px-5 py-2.5 dark:border-gray-700 dark:bg-gray-900/40">
            <dl className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
              {(opened.retry?.count ?? 0) > 0 && (
                <div className="flex items-center gap-1.5">
                  <dt className="text-gray-400">Retries:</dt>
                  <dd className="font-semibold text-gray-600 dark:text-gray-300">{opened.retry?.count}</dd>
                </div>
              )}
              {opened.retry?.nextAttemptAt && (
                <div className="flex items-center gap-1.5">
                  <dt className="text-gray-400">Next attempt:</dt>
                  <dd className="font-semibold text-gray-600 dark:text-gray-300">{fmt(opened.retry.nextAttemptAt)}</dd>
                </div>
              )}
              {opened.deliveredAt && (
                <div className="flex items-center gap-1.5">
                  <dt className="text-gray-400">Delivered:</dt>
                  <dd className="font-semibold text-gray-600 dark:text-gray-300">{fmt(opened.deliveredAt)}</dd>
                </div>
              )}
              {opened.sla?.dueAt && (
                <div className="flex items-center gap-1.5">
                  <dt className="text-gray-400">SLA due:</dt>
                  <dd className="font-semibold text-gray-600 dark:text-gray-300">{fmt(opened.sla.dueAt)}</dd>
                </div>
              )}
            </dl>
          </footer>
        )}
      >
        {opened && (
          <article className="flex flex-col gap-3 px-5 py-4">
            {/* Where it was actually addressed. The name above says who; on a
                failed send the question is which address we tried. */}
            {Object.entries(recipientByRef.get(opened.recipientRef)?.contact || {}).length > 0 && (
              <dl className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                {Object.entries(recipientByRef.get(opened.recipientRef)?.contact || {})
                  .filter(([, v]) => !!v)
                  .map(([k, v]) => (
                    <div key={k} className="flex items-center gap-1.5">
                      <dt className="text-gray-400">{humanise(k)}:</dt>
                      <dd className="font-medium text-gray-600 dark:text-gray-300 break-all">{v}</dd>
                    </div>
                  ))}
              </dl>
            )}

            {/* Why it did not go, in full — the row above had room for one line. */}
            {(facts(opened).reason || facts(opened).lastError) && (
              <div className="rounded border-l-2 border-red-400 bg-red-50 px-3 py-2 dark:bg-red-950/30">
                {facts(opened).reason && (
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-400" title={opened.reasonCode || undefined}>
                    {facts(opened).reason}
                  </p>
                )}
                {facts(opened).lastError && (
                  <p className="mt-0.5 text-xs text-red-600 break-words dark:text-red-400">
                    {facts(opened).lastError}
                  </p>
                )}
              </div>
            )}

            {/* What the gateway reported after the send — the status tag above
                stays SENT because it only answers "did our send succeed?". */}
            {opened.channel === 'whatsapp' && opened.whatsapp && (
              <WhatsAppProgress whatsapp={opened.whatsapp} replies={opened.replies} />
            )}

            {opened.renderedContent ? (
              <SentMessage content={opened.renderedContent} />
            ) : (
              <span className="text-xs text-gray-400">
                Nothing was sent on this delivery, so there is no message to show.
              </span>
            )}

            {/* Every try, in order, with what the provider said each time. The
                status is the last line of this story; a delivery that took four
                goes and a delivery that worked first time look identical without
                it, and the difference is usually the thing being looked for. */}
            {(opened.attempts || []).length > 0 && (
              <section>
                <p className="text-xs font-medium text-ink-muted mb-1">
                  Attempts ({(opened.attempts || []).length})
                </p>
                <ol className="flex flex-col gap-1">
                  {(opened.attempts || []).map((raw, n) => {
                    const at = raw as { at?: string; success?: boolean; reasonCode?: string; detail?: Record<string, unknown> };
                    const note = detailOf(at.detail);
                    return (
                      <li key={n} className="flex items-start gap-2 text-xs">
                        <span className="shrink-0 font-mono text-gray-400">{fmt(at.at)}</span>
                        <Tag value={at.success ? 'OK' : 'FAILED'} severity={at.success ? 'success' : 'danger'}
                          className="text-xs px-1.5 py-0.5" />
                        <span className="min-w-0 break-words text-gray-600 dark:text-gray-300">
                          {at.reasonCode ? reasonLabel(at.reasonCode) : ''}
                          {at.reasonCode && note ? ' — ' : ''}
                          {note}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </section>
            )}
          </article>
        )}
      </SideDrawer>
    </div>
  );
};

export default NotificationRunView;
